import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Typography, Collapse, Divider, Grid } from 'antd';
import ComponentErrorBoundary from 'components/shared-components/ComponentErrorBoundary';
import formatCurrency from 'utils/currencyFormatter';
import { usePriceAdminWebSocket } from 'hooks/usePriceAdminWebSocket';
import StatusTable from './StatusTable';
import CustomerTablePreview from 'components/util-components/live-quote-table/GoldPriceTable';
import Flex from 'components/shared-components/Flex';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

export const CurrentPriceInfo = () => {
  const { dataManager, isConnected } = usePriceAdminWebSocket();
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [priceData, setPriceData] = useState([]);
  const [data, setData] = useState({
    'XAU/MYR': {
      FeedBuyPrice: 0,
      FeedSellPrice: 0,
    },
  });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const screens = useBreakpoint();

  useEffect(() => {
    try {
      if (!dataManager || !isConnected) return;

      // Subscribe to dedicated CurrentPriceInfo events - completely separate from manual override
      const unsubscribeFeed = dataManager.subscribe(
        'currentPriceInfoBaseFeed',
        (feedData) => {
          try {
            setData(dataManager.getCurrentPriceInfoFeedData());
          } catch (error) {
            console.error('Error processing feed data:', error);
            // Just log the error instead of throwing to error boundary
          }
        },
      );

      const unsubscribePrice = dataManager.subscribe(
        'currentPriceInfoCompanyData',
        (companyData) => {
          try {
            // Update price data whenever company data is received
            if (companyData && Object.keys(companyData).length > 0) {
              const formattedData = dataManager.getCurrentPriceInfoPriceData();
              console.log(
                'CurrentPriceInfo: Received company data:',
                companyData,
              );
              console.log(
                'CurrentPriceInfo: Formatted price data:',
                formattedData,
              );
              setPriceData(formattedData);
            }
          } catch (error) {
            console.error('Error processing company data:', error);
            // Just log the error instead of throwing to error boundary
          }
        },
      );

      // Request initial data using dedicated endpoint
      dataManager.requestCurrentPriceInfoData();

      return () => {
        unsubscribeFeed();
        unsubscribePrice();
      };
    } catch (error) {
      console.error('Error setting up CurrentPriceInfo subscriptions:', error);
      // Just log the error instead of throwing to error boundary
    }
  }, [dataManager, isConnected]); // Removed handleAsyncError from dependencies

  useEffect(() => {
    if (!dataManager) return;

    const intervalId = setInterval(() => {
      setFetchTrigger(Math.random());
    }, 3000);

    return () => clearInterval(intervalId);
  }, [dataManager]);

  useEffect(() => {
    if (dataManager) {
      // Use dedicated CurrentPriceInfo endpoint - completely isolated from manual override
      dataManager.requestCurrentPriceInfoData();
    }
  }, [dataManager, fetchTrigger]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const parsePrice = (price) => {
    if (price != 0) {
      return '' + formatCurrency(price);
    } else {
      return 'N/A';
    }
  };

  return (
    // <Card>
    <Collapse defaultActiveKey={['1']} style={{ marginBottom: 12 }}>
      <Collapse.Panel
        header={
          <Title level={5} style={{ margin: 0 }}>
            Current Price Info
          </Title>
        }
        key="1"
        style={{ padding: 0 }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col
            xs={24}
            sm={24}
            md={8}
            lg={8}
            xl={6}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            <Card
              bodyStyle={{ padding: 12 }}
              bordered={false}
              style={{ width: '100%', minHeight: 180, margin: 0 }}
            >
              <div style={{ marginRight: 8 }}>
                <Title level={5} style={{ marginBottom: 8 }}>
                  XAU - 1g (Gold)
                </Title>
                <Text type="secondary">
                  Feed Buy:{' '}
                  <strong>
                    {data['XAU/MYR'].FeedBuyPrice != 'N/A'
                      ? parsePrice(data['XAU/MYR']?.FeedBuyPrice)
                      : 'N/A'}
                  </strong>
                </Text>
                <br />
                <Text type="secondary">
                  Feed Sell:{' '}
                  <strong>
                    {data['XAU/MYR'].FeedSellPrice != 'N/A'
                      ? parsePrice(data['XAU/MYR']?.FeedSellPrice)
                      : 'N/A'}
                  </strong>
                </Text>
                <br />
                <br />
                <Text
                  type="secondary"
                  style={{ fontSize: '12px', color: '#888' }}
                >
                  Only XAU/MYR (Gold) feed is supported. Tomei processes GOLD -
                  1g only.
                </Text>
              </div>
            </Card>
          </Col>
          <Col
            xs={24}
            sm={12}
            md={8}
            lg={8}
            xl={9}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {screens.xs && <Divider style={{ margin: '8px 0' }} />}
            <CustomerTablePreview
              liveQuotePrice={priceData}
              updatedAt={new Date()}
              style={{ minHeight: 180 }}
            />
          </Col>
          <Col
            xs={24}
            sm={12}
            md={8}
            lg={8}
            xl={9}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {screens.xs && <Divider style={{ margin: '8px 0' }} />}
            <Card
              bodyStyle={{ padding: 12 }}
              bordered={false}
              style={{ width: '100%', minHeight: 180, margin: 0 }}
            >
              <StatusTable priceData={data} />
            </Card>
          </Col>
        </Row>
      </Collapse.Panel>
    </Collapse>
    // </Card>
  );
};

export default React.memo(CurrentPriceInfo);
